import { useMemo } from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useProviderLanguage } from '@/hooks/useProviderLanguage';
import { useVoiceSTT } from '@/hooks/useVoiceProviders';
import { VoiceIntegration } from '@/shared/types/integration';
import { buildVoiceLanguageOptions } from '@/util/languageOptions';

import { syncDeepgramModel } from '../../Components/syncDeepgramModel';

/**
 * Language selector for voice integrations. Writes the canonical `config.language` and cascades it
 * to every provider config (via `useProviderLanguage`), keeping the Deepgram model in sync.
 */
const LanguageField = () => {
  const { t } = useTranslation();
  const { control, setValue, watch } = useFormContext<VoiceIntegration>();
  const { languageValue, setLanguage } = useProviderLanguage();
  const { models: sttModels } = useVoiceSTT();

  const languageOptions = useMemo(() => buildVoiceLanguageOptions(languageValue), [languageValue]);

  const handleLanguageChange = (languageCode: string) => {
    setLanguage(languageCode);
    if (sttModels) {
      syncDeepgramModel(languageCode, sttModels, { setValue, watch });
    }
  };

  return (
    <FormField
      control={control}
      name="config.language"
      render={() => (
        <FormItem>
          <FormLabel>{t('field.language')}</FormLabel>
          <FormControl>
            <PopoverCombobox
              options={languageOptions}
              value={languageValue}
              onChange={handleLanguageChange}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default LanguageField;
